import React, { useState, useEffect, useCallback } from "react";
import { Card, Grid, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useMaterialUIController } from "context";
import { useSnackbar } from "notistack";
import useApiRequest from "utils/Apirequest";
import Loader from "components/custom/Loader";
import CustomMaterialTable from "components/custom/CustomMaterialTable";
import { AutoTooltip } from "utils/Constant";
import { getDecodedUserData } from "utils/Constant";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import BulkUploadDialog from "components/BulkUpload/BulkUploadDialog";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MDButton from "components/MDButton";
import CloseIcon from "@mui/icons-material/Close";

function BulkUploadLogs() {
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;
  const { enqueueSnackbar } = useSnackbar();
  const apiRequest = useApiRequest();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState("driver");
  const [failedRowsDialog, setFailedRowsDialog] = useState({ open: false, rows: [], jobId: '' });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await apiRequest(
        "get",
        "bulk-upload/logs",
        null,
        {},
        {},
        false
      );

      if (data?.status) {
        setLogs(data.data || []);
      } else {
        enqueueSnackbar(data?.message || "Failed to fetch logs", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      enqueueSnackbar("Failed to fetch bulk upload logs", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, enqueueSnackbar]);

  useEffect(() => {
    if (getDecodedUserData()?.login_status !== "true") {
      navigate("/sign-in");
      return;
    }
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status) => {
    const statusColors = {
      PENDING: "warning",
      PROCESSING: "info",
      COMPLETED: "success",
      FAILED: "error",
    };
    return statusColors[status] || "default";
  };

  const columns = [
    {
      header: "Job ID",
      accessorKey: "jobId",
      muiTableHeadCellProps: { align: "left" },
      muiTableBodyCellProps: { align: "left" },
      Cell: ({ cell }) => (
        <AutoTooltip maxWidth={200}>{cell.getValue()}</AutoTooltip>
      ),
    },
    {
      header: "Module",
      accessorKey: "module",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()?.toUpperCase()}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          color={getStatusColor(cell.getValue())}
          size="small"
          sx={{ fontWeight: 'bold',  color: cell.getValue().toLowerCase() === "completed" ? "#FFF" : "inherit" }}
        />
      ),
    },
    {
      header: "Uploaded By",
      accessorKey: "uploadedBy",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => {
        const uploadedBy = row.original.uploadedBy;
        return uploadedBy?.username || uploadedBy?.email || "N/A";
      },
    },
    {
      header: "Total Records",
      accessorKey: "totalRecords",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => cell.getValue() || 0,
    },
    {
      header: "Success",
      accessorKey: "successCount",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => {
        const count = cell.getValue() || 0;
        return (
          <Chip
            label={count}
            color={count > 0 ? "success" : "default"}
            size="small"
            sx={{ fontWeight: 'bold', color: count > 0 ? "#FFF" : "inherit" }}
          />
        );
      },
    },
    {
      header: "Failed",
      accessorKey: "failedCount",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => {
        const count = cell.getValue() || 0;
        return (
          <Chip
            label={count}
            color={count > 0 ? "error" : "default"}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      header: "Started At",
      accessorKey: "startedAt",
      enableColumnFilter : false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      header: "Finished At",
      accessorKey: "finishedAt",
      enableColumnFilter : false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      header: "Failed Rows",
      accessorKey: "failedRows",
      enableColumnFilter: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => {
        const failedRows = row.original.failedRows || [];
        if (failedRows.length === 0) return "None";

        return (
          <Chip 
            icon={<VisibilityIcon fontSize="small" />}
            label={`${failedRows.length} Failed`}
            size="small"
            color="error"
            variant="outlined"
            clickable
            onClick={() => {
              console.log('Failed Rows Data:', failedRows);
              console.log('First failed row:', failedRows[0]);
              setFailedRowsDialog({
                open: true,
                rows: failedRows,
                jobId: row.original.jobId || 'Unknown'
              });
            }}
            sx={{ cursor: 'pointer' }}
          />
        );
      },
    },
  ];

  return (
    <DashboardLayout>
      <BulkUploadDialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        moduleName={selectedModule}
        onUploadSuccess={() => {
          fetchLogs();
        }}
      />
      
      {/* Failed Rows Dialog */}
      <Dialog 
        open={failedRowsDialog.open} 
        onClose={() => setFailedRowsDialog({ open: false, rows: [], jobId: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5">
              Failed Rows Details - Job: {failedRowsDialog.jobId}
            </MDTypography>
            <IconButton 
              onClick={() => setFailedRowsDialog({ open: false, rows: [], jobId: '' })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: sidenavColor === 'error' ? '#d32f2f' : '#1976d2', color: '#000' }}>
                    Row Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: sidenavColor === 'error' ? '#d32f2f' : '#1976d2', color: '#000' }}>
                    Error Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedRowsDialog.rows.map((fr, idx) => {
                  console.log(`Row ${idx}:`, fr);
                  return (
                    <TableRow 
                      key={idx}
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' },
                        '&:hover': { backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'bold', color: sidenavColor === 'error' ? '#d32f2f' : '#1976d2', width: '150px' }}>
                        Row #{fr.row || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: darkMode ? '#fff' : '#000', wordBreak: 'break-word' }}>
                        {fr.error || fr.message || JSON.stringify(fr) || 'No error details available'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <MDBox mt={2}>
            <MDTypography variant="caption" color="text">
              Total Failed Rows: {failedRowsDialog.rows.length}
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setFailedRowsDialog({ open: false, rows: [], jobId: '' })}
            variant="contained"
            sx={{
              backgroundColor: sidenavColor === 'error' ? '#d32f2f' : '#1976d2',
              color: '#fff',
              '&:hover': {
                backgroundColor: sidenavColor === 'error' ? '#c62828' : '#1565c0',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={1.5}
                px={2}
                variant="gradient"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Bulk Upload Logs
                  </MDTypography>
                  <MDBox display="flex" gap={1} alignItems="center">
                    <MDButton
                      onClick={() => {
                        setSelectedModule("driver");
                        setBulkUploadOpen(true);
                      }}
                      variant="outlined"
                      color="white"
                      size="small"
                      startIcon={<UploadFileIcon />}
                    >
                      Upload Drivers
                    </MDButton>
                    <MDButton
                      onClick={() => {
                        setSelectedModule("vehicle");
                        setBulkUploadOpen(true);
                      }}
                      variant="outlined"
                      color="white"
                      size="small"
                      startIcon={<UploadFileIcon />}
                    >
                      Upload Vehicles
                    </MDButton>
                    <Tooltip title="Refresh">
                      <IconButton onClick={fetchLogs} size="small">
                        <RefreshIcon style={{ color: "white" }} />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                </Grid>
              </MDBox>
              {isLoading ? (
                <Loader />
              ) : (
                <CustomMaterialTable
                  columns={columns}
                  data={logs}
                  manualPagination={false}
                  enablePagination={true}
                  initialState={{
                    pagination: { pageSize: 10, pageIndex: 0 },
                    sorting: [{ id: "createdAt", desc: true }],
                  }}
                  muiTableProps={{
                    sx: darkMode
                      ? {
                          backgroundColor: "#202940",
                          color: "#ffffff",
                          "& td": {
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#ffffff",
                            padding: 1,
                          },
                        }
                      : {
                          "& td": {
                            fontSize: "14px",
                            fontWeight: "500",
                            padding: 1,
                            backgroundColor: "#f5f5f5",
                          },
                        },
                  }}
                  muiTopToolbarProps={{
                    sx: darkMode
                      ? {
                          color: "#ffffff",
                          "& svg": {
                            color: "#ffffff",
                          },
                        }
                      : {
                          backgroundColor: "#f5f5f5",
                        },
                  }}
                  muiTableHeadCellProps={{
                    sx: darkMode
                      ? {
                          color: "#ffffff",
                          "& svg": {
                            color: "#ffffff",
                          },
                        }
                      : {
                          backgroundColor: "#f5f5f5",
                        },
                  }}
                  muiBottomToolbarProps={{
                    sx: darkMode
                      ? {
                          color: "#ffffff",
                          "& p,button,div": {
                            color: "#ffffff",
                          },
                        }
                      : {
                          backgroundColor: "#f5f5f5",
                        },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      borderBottom: "2px solid #e0e0e0",
                    },
                  }}
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default BulkUploadLogs;
